import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { School, Settings, UserPlus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { auth, functions } from "../firebase/config";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import Teacherheader from "./StudentHeader";
import Teachersidebar from "./StudentSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";

type SchoolItem = { id: string; schoolName: string; role: string };

const StudentSettings = () => {
  const [email, setEmail] = useState("");
  const [schools, setSchools] = useState<SchoolItem[]>([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [enrolledSchoolName, setEnrolledSchoolName] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setEmail(user.email || "");
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setEmail("");
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        if (!email || !isAuthenticated) {
          return;
        }
        console.log("Fetching schools for email:", email.toLowerCase());
        
        const getUserInvitations = httpsCallable(functions, 'getUserInvitations');
        const result = await getUserInvitations();
        const data = result.data as { invitations: any[] };
        
        const schoolList: SchoolItem[] = [];

        if (data.invitations) {
          for (const inviteData of data.invitations) {
            // Filter for student role and pending status
            if (inviteData.role === "student" && inviteData.status === "pending") {
              const schoolID = inviteData.schoolID;
              const schoolName = inviteData.schoolName || inviteData.originalSchoolName || "Unnamed School";
              console.log("Found invitation:", { schoolID, schoolName, role: inviteData.role });

              if (!schoolList.some((s) => s.id === schoolID)) {
                schoolList.push({ id: schoolID, schoolName, role: inviteData.role });
              }
            }
          }
        }

        console.log("Schools loaded:", schoolList);
        setSchools(schoolList);
      } catch (err) {
        console.error("Error fetching schools from invitations:", err);
        toast.error("Failed to load schools list.");
      } finally {
        setLoadingSchools(false);
      }
    };

    if (isAuthenticated) {
      fetchSchools();
    }
  }, [email, isAuthenticated]);

  const handleEnrollClick = async () => {
    if (!isAuthenticated || !auth.currentUser) {
      console.log("Authentication check failed:", { isAuthenticated, currentUser: !!auth.currentUser });
      toast.error("User not authenticated. Please log in again.");
      return;
    }
    if (!selectedSchool) {
      console.log("No school selected");
      toast.error("Please select a school.");
      return;
    }

    setIsEnrolling(true);
    console.log("handleEnrollClick started:", { email, selectedSchool, uid: auth.currentUser.uid });
    try {
      const currentUid = auth.currentUser.uid;

      // Find the selected school in our state to get the role
      const selectedSchoolData = schools.find(s => s.id === selectedSchool);
      
      if (!selectedSchoolData) {
        console.log("No matching invitation found in state");
        toast.error("No valid invitation found for this school.");
        return;
      }

      const inviteRole = selectedSchoolData.role;
      const inviteSchoolID = selectedSchoolData.id;
      console.log("Invitation data:", { inviteRole, inviteSchoolID, schoolName: selectedSchoolData.schoolName });

      // Call setClaims cloud function
      console.log("Calling setClaims with:", { uid: currentUid, role: inviteRole, schoolID: inviteSchoolID, setAsDefault: true });
      const setClaims = httpsCallable(functions, "setClaims");
      const result = await setClaims({
        uid: currentUid,
        role: inviteRole,
        schoolID: inviteSchoolID,
        setAsDefault: true,
      });
      const data = result.data as { schoolID: string };
      console.log("setClaims response:", data);

      if (data.schoolID !== inviteSchoolID) {
        console.log("SchoolID mismatch in response:", { expected: inviteSchoolID, received: data.schoolID });
        throw new Error("Failed to set schoolID correctly.");
      }

      // Refresh token to apply new claims
      await auth.currentUser.getIdToken(true);
      console.log("Token refreshed after setClaims");

      // Show modal popup
      setEnrolledSchoolName(selectedSchoolData.schoolName || "Unnamed School");
      setShowSuccessModal(true);

      toast.success(`Successfully enrolled in ${selectedSchoolData.schoolName || "Unnamed School"}!`, {
        description: `Enrollment completed at ${new Date().toLocaleTimeString("en-US", { timeZone: "Africa/Nairobi" })} EAT, ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}.`,
        duration: 4000,
      });
    } catch (err: any) {
      console.error("Enrollment error:", {
        message: err.message,
        code: err.code,
        details: err.details,
        stack: err.stack,
      });
      toast.error("An unexpected error occurred during enrollment. Check console for details.");
    } finally {
      console.log("Enrollment process completed, resetting isEnrolling");
      setIsEnrolling(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <div className="hidden lg:block w-64 h-full border-r border-gray-200 dark:border-gray-700">
          <Teachersidebar />
        </div>
        <div className="flex flex-col flex-1 h-full">
          <Teacherheader />
          <main className="flex-1 overflow-auto p-6 bg-gray-50 dark:bg-[#0f1117]">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Student Settings
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage your account and enrollment settings
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="rounded-xl border border-gray-200 dark:border-[#2C2C3B] bg-white dark:bg-[#1E1E2D]">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                      <School className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                        Enroll to School
                      </CardTitle>
                      <CardDescription className="text-gray-600 dark:text-gray-400">
                        Join a new school as a student
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="dark:text-gray-300">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      readOnly
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="school">Select a School</Label>
                    <Select
                      value={selectedSchool}
                      onValueChange={setSelectedSchool}
                      disabled={loadingSchools || !isAuthenticated}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={loadingSchools ? "Loading…" : "Choose a school"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.length === 0 ? (
                          <div className="px-4 py-2 text-gray-500 text-sm">You have not been enrolled in any school</div>
                        ) : (
                          schools.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.schoolName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleEnrollClick}
                    className="w-full bg-blue-600 text-white mt-4 hover:bg-blue-700"
                    disabled={!email || !selectedSchool || isEnrolling || !isAuthenticated}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {isEnrolling ? "Enrolling..." : "Enroll"}
                  </Button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Your email must match an invitation sent by the school admin.
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-xl border border-gray-200 dark:border-[#2C2C3B] bg-white dark:bg-[#1E1E2D]">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-500 rounded-xl flex items-center justify-center">
                      <Settings className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                        Account Settings
                      </CardTitle>
                      <CardDescription className="text-gray-600 dark:text-gray-400">
                        Manage your profile and preferences
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-3">
                    <Button variant="outline" className="w-full justify-start border-zinc-300">
                      Update Profile
                    </Button>
                    <Button variant="outline" className="w-full justify-start border-zinc-300">
                      Change Password
                    </Button>
                    <Button variant="outline" className="w-full justify-start border-zinc-300">
                      Notification Preferences
                    </Button>
                    <Button variant="outline" className="w-full justify-start border-zinc-300">
                      Privacy Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Success Modal Popup */}
            {showSuccessModal && (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <div className="bg-white dark:bg-[#23233a] rounded-xl shadow-xl p-8 w-full max-w-md mx-auto flex flex-col items-center">
                  <div className="flex items-center justify-center mb-4">
                    <UserPlus className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">Successfully Enrolled!</h2>
                  <p className="text-gray-700 dark:text-gray-300 text-center mb-4">
                    You have been successfully enrolled as a <span className="font-semibold text-blue-600">student</span> at <span className="font-semibold text-green-600">{enrolledSchoolName}</span>.
                  </p>
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      window.location.href = "/student-dashboard";
                    }}
                    className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 focus:outline-none"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default StudentSettings;